import { browserSessionStorage } from "../lib/browser-storage.ts";
import { useEffect, useRef, useState } from "react";
import { AlertCircle, ShieldCheck } from "lucide-react";
import { apiFetch, enableDirectApiSession, type ApiSessionInfo, type ApiUser } from "../lib/api";
import { completeStudentCareReportsUnlock, saveToken } from "../lib/auth";
import { safeAppReturnPath } from "../lib/authReturnPath";
import { isNativeApp, takeNativeLoginVerifier, completeNativeLogin } from "../lib/native-app";
import { nativeLoginCallbackUrl } from "../lib/native-auth-link";
import { confirmLoginSession, initialSessionCheckTimeoutMs } from "../lib/session-check";
import { translations, type Locale } from "../lib/i18n";

export function AuthCallback({ locale }: { locale: Locale }) {
  const t = translations[locale];
  const [error, setError] = useState<string | null>(null);
  const [appHandoffUrl, setAppHandoffUrl] = useState<string | null>(null);

  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const errorMessage = params.get("error");
    const errorDescription = params.get("error_description");
    const microsoftOAuthCallback = window.location.pathname === "/api/auth/microsoft/callback";
    const unlockStudentCareReports = params.get("student_care_reports") === "1";
    const normalizeCleanupReturn = (value: string | null) => !value || value === "/?module=settings&settings=cleanup"
      ? "/dashboard?module=settings&settings=cleanup"
      : value;
    const resolveRedirectPath = (callbackParams: URLSearchParams, cleanupFromServer = false) => {
      const cleanupReauth = cleanupFromServer || callbackParams.get("cleanup_reauth") === "1";
      const userDeleteReauth = callbackParams.get("user_delete_reauth") === "1";
      const cleanupReturnTo = normalizeCleanupReturn(callbackParams.get("return_to"));
      const studentCareView = callbackParams.get("student_care_view");
      const studentCareReturnPath = studentCareView === "professorReports" || studentCareView === "counselorReports"
        ? `/?module=studentCare&student_care_view=${studentCareView}`
        : "/?module=studentCare";
      const appReturnPath = safeAppReturnPath(callbackParams.get("app_return_to"), window.location.origin);
      return cleanupReauth
        ? cleanupReturnTo
        : userDeleteReauth
          ? (callbackParams.get("return_to") || "/dashboard?module=users")
        : appReturnPath || (callbackParams.get("target_module") === "studentCare" ? studentCareReturnPath : "/");
    };

    if (errorMessage) {
      setError(errorDescription || errorMessage);
      return;
    }
    if (!code || (microsoftOAuthCallback && !state)) {
      setError(t.callbackError);
      return;
    }

    const finishMicrosoftCallback = microsoftOAuthCallback
      ? apiFetch<{ code: string; returnTo: string; cleanupReauth: boolean }>("/api/auth/microsoft/finish", {
          method: "POST",
          body: JSON.stringify({ code, state }),
        })
      : Promise.resolve({ code, returnTo: `${window.location.origin}/auth/callback${window.location.search}`, cleanupReauth: params.get("cleanup_reauth") === "1" });

    finishMicrosoftCallback
      .then(async (finished) => {
        const callbackUrl = new URL(finished.returnTo, window.location.origin);
        // Microsoft login started by the mobile app finishes in the system browser; hand the code back to the app.
        if (!isNativeApp() && callbackUrl.searchParams.get("native") === "1") {
          const handoffUrl = nativeLoginCallbackUrl(finished.code);
          setAppHandoffUrl(handoffUrl);
          window.location.assign(handoffUrl);
          return null;
        }
        const redirectPath = resolveRedirectPath(callbackUrl.searchParams, finished.cleanupReauth);
        const shouldUnlockStudentCareReports = unlockStudentCareReports || callbackUrl.searchParams.get("student_care_reports") === "1";
        const verifier = isNativeApp() ? await takeNativeLoginVerifier() : null;
        const result = await apiFetch<{ session: ApiSessionInfo; user: ApiUser }>("/api/auth/session/exchange", {
          method: "POST",
          body: JSON.stringify(verifier ? { code: finished.code, verifier } : { code: finished.code }),
        });
        if (isNativeApp()) await completeNativeLogin();
        return {
          result,
          redirectPath,
          cleanupReauth: finished.cleanupReauth || callbackUrl.searchParams.get("cleanup_reauth") === "1",
          unlockStudentCareReports: shouldUnlockStudentCareReports,
        };
      })
      .then(async (completed) => {
        if (!completed) return;
        const { result, redirectPath, cleanupReauth, unlockStudentCareReports } = completed;
        const verified = await confirmLoginSession(result.user.id, () => apiFetch<{ session: ApiSessionInfo; user: ApiUser }>("/api/auth/me", {
          suppressUnauthorizedSessionEvent: true, signal: AbortSignal.timeout(initialSessionCheckTimeoutMs),
        }));
        saveToken(null, verified.session);
        await enableDirectApiSession(result.user.id);
        if (unlockStudentCareReports) {
          completeStudentCareReportsUnlock(result.user.id, { allowWithoutPending: true });
        } else {
          completeStudentCareReportsUnlock(result.user.id);
        }
        if (cleanupReauth) {
          browserSessionStorage.setItem("edubgt_cleanup_reauth_returned", "1");
        }
        window.history.replaceState({}, "", redirectPath);
        window.location.replace(redirectPath);
      })
      .catch((requestError: Error) => setError(requestError.message || t.callbackError));
  }, [t.callbackError]);

  return (
    <main className="callback-screen">
      <a className="callback-brand" href="/">
        <img src="/assets/bgt-logo.png" alt="BGT" />
        <span>{t.productName}</span>
      </a>
      <section className={`callback-state${error ? " error" : ""}`}>
        <span className="callback-icon">{error ? <AlertCircle /> : <ShieldCheck />}</span>
        <h1>{error ? t.callbackError : appHandoffUrl ? "Kthehu te aplikacioni eduBGT" : t.callbackTitle}</h1>
        <p>{error || (appHandoffUrl ? "Kyçja përfundoi. Nëse aplikacioni nuk u hap vetë, shtyp butonin më poshtë." : t.callbackText)}</p>
        {error ? <a className="primary-link" href="/">{t.retry}</a> : appHandoffUrl ? <a className="primary-link" href={appHandoffUrl}>Hap aplikacionin</a> : <div className="callback-loader"><span /></div>}
      </section>
    </main>
  );
}
